import React, { useId } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import {
  AppBadge, HelpText, IconButton, Label, NativeSelect,
} from '../ui';

// ─── CoursePicker — an ordered pick of courses for the website ───────────
//
// Stores course ids, in order. The website shows only published courses, so
// an unpublished one can still be seen in the list (it was picked while
// published, or has been unpublished since) but cannot be added — and says
// why, rather than vanishing from the choices without explanation.
//
// Props:
//   value     — array of course ids
//   onChange  — (ids) => void
//   courses   — [{ id, name, isPublished }] from /cms/courses
//   max       — how many the website shows
//   label, hint

export default function CoursePicker({ value, onChange, courses = [], max = 6, label, hint }) {
  const id = useId();
  const picked = Array.isArray(value) ? value.filter((v) => typeof v === 'string') : [];
  const byId = new Map(courses.map((c) => [c.id, c]));
  const available = courses.filter((c) => !picked.includes(c.id));
  const full = picked.length >= max;

  const move = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= picked.length) return;
    const next = [...picked];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-2">
      {label && <Label htmlFor={`${id}-add`}>{label}</Label>}

      {picked.length > 0 && (
        <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1.5 tw-p-0">
          {picked.map((courseId, i) => {
            const course = byId.get(courseId);
            return (
              <li
                key={courseId}
                className="tw-flex tw-items-center tw-gap-2 tw-rounded-sm tw-border tw-border-solid tw-border-line tw-bg-surface-raised tw-py-1.5 tw-pl-3 tw-pr-1.5"
              >
                <span className="tw-w-5 tw-shrink-0 tw-text-xs tw-tabular-nums tw-text-content-subtle">{i + 1}.</span>
                <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-text-content">
                  {course ? course.name : 'A course that has been deleted'}
                </span>
                {course && !course.isPublished && (
                  <AppBadge variant="outline" size="sm">Not published — hidden</AppBadge>
                )}
                <IconButton size="xs" icon={ChevronUp} label="Move up" onClick={() => move(i, -1)} disabled={i === 0} />
                <IconButton size="xs" icon={ChevronDown} label="Move down" onClick={() => move(i, 1)} disabled={i === picked.length - 1} />
                <IconButton
                  size="xs"
                  icon={X}
                  variant="ghost-destructive"
                  label={`Remove ${course?.name || 'course'}`}
                  onClick={() => onChange(picked.filter((p) => p !== courseId))}
                />
              </li>
            );
          })}
        </ol>
      )}

      <NativeSelect
        id={`${id}-add`}
        value=""
        disabled={full || available.length === 0}
        onChange={(e) => { if (e.target.value) onChange([...picked, e.target.value]); }}
        aria-describedby={hint ? `${id}-hint` : undefined}
      >
        <option value="">
          {full
            ? `All ${max} places are filled — remove one to add another`
            : courses.length === 0 ? 'You have no courses yet' : 'Add a course…'}
        </option>
        {available.map((c) => (
          <option key={c.id} value={c.id} disabled={!c.isPublished}>
            {c.isPublished ? c.name : `${c.name} (not published)`}
          </option>
        ))}
      </NativeSelect>

      {hint && <HelpText id={`${id}-hint`}>{hint}</HelpText>}
    </div>
  );
}
